import { t, getLang, useLang } from '../i18n';
// 訳す先の言語と、マイク環境（雑音の抑え方）を選ぶ
export const MAIN_TARGETS: { code: string; label: string }[] = [
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English（英語）' },
  { code: 'es', label: 'Español（スペイン語）' },
  { code: 'de', label: 'Deutsch（ドイツ語）' },
  { code: 'fr', label: 'Français（フランス語）' },
  { code: 'zh', label: '中文（中国語）' },
  { code: 'ko', label: '한국어（韓国語）' },
];
export const TRIAL_TARGETS: { code: string; label: string }[] = [
  { code: 'it', label: 'Italiano（イタリア語）' },
  { code: 'pt', label: 'Português（ポルトガル語）' },
  { code: 'th', label: 'ไทย（タイ語）' },
  { code: 'vi', label: 'Tiếng Việt（ベトナム語）' },
  { code: 'id', label: 'Bahasa Indonesia（インドネシア語）' },
  { code: 'tl', label: 'Filipino（フィリピン語）' },
];

// 表示名：日本語画面では「Español（スペイン語）」、英語画面では「Español (Spanish)」
const EN_NAMES: Record<string, string> = { ja: 'Japanese', en: 'English', es: 'Spanish', de: 'German', fr: 'French', zh: 'Chinese', ko: 'Korean', it: 'Italian', pt: 'Portuguese', th: 'Thai', vi: 'Vietnamese', id: 'Indonesian', tl: 'Filipino' };
const NATIVE = (x: { code: string; label: string }) => x.label.replace(/（.*）/, '');
const tl = (x: { code: string; label: string }) => getLang() === 'en' ? (x.code === 'en' ? 'English' : x.code === 'ja' ? '日本語 (Japanese)' : (NATIVE(x) === EN_NAMES[x.code] ? NATIVE(x) : `${NATIVE(x)} (${EN_NAMES[x.code]})`)) : x.label;

export type NoiseMode = '' | 'far_field' | 'near_field';

interface Props {
  target: string;
  onTargetChange: (code: string) => void;
  noise: NoiseMode;
  onNoiseChange: (mode: NoiseMode) => void;
  second?: string;
  onSecondChange?: (code: string) => void;
}

const selectStyle = { width: '100%', padding: '10px', fontSize: '15px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff' };

export default function TargetSelect({ target, onTargetChange, noise, onNoiseChange, second = '', onSecondChange }: Props) {
  useLang();
  const all = [...MAIN_TARGETS, ...TRIAL_TARGETS];
  const current = all.find((t) => t.code === target);
  const isTrial = TRIAL_TARGETS.some((t) => t.code === target);
  return (
    <div className="card">
      <div className="card-title">
        {t('訳す先の言語', 'Translate into')}
        <span className="shortcut-hint" style={{ marginLeft: '0.5rem' }}>
          <kbd>1</kbd> 日本語 / <kbd>2</kbd> English
        </span>
      </div>

      <select id="target-lang" value={target} onChange={(e) => onTargetChange(e.target.value)} style={selectStyle}>
        <optgroup label={t('対応言語', 'Supported')}>
          {MAIN_TARGETS.map((x) => <option key={x.code} value={x.code}>{tl(x)}</option>)}
        </optgroup>
        <optgroup label={t('試験提供', 'Trial')}>
          {TRIAL_TARGETS.map((x) => <option key={x.code} value={x.code}>{tl(x)}{t('（試験提供）', ' · trial')}</option>)}
        </optgroup>
      </select>

      <div style={{ marginTop: '0.75rem', textAlign: 'center', padding: '0.5rem', background: '#eff6ff', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 800, color: '#1a56db' }}>
        {t('話す言語（自動判定）', 'Spoken language (auto-detected)')} → {current ? NATIVE(current) : target}
      </div>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', lineHeight: 1.6 }}>
        {t('話している言語はAIが自動で判定します。選んだ言語に訳した字幕を表示します。', 'The AI detects the spoken language automatically and shows captions in the language you choose.')}
        {isTrial && <><br /><b style={{ color: '#b45309' }}>{t('試験提供の言語は、翻訳の精度を確認中です。', 'Trial languages are still being evaluated for accuracy.')}</b></>}
      </p>

      {onSecondChange && (
        <>
          <div className="card-title" style={{ marginTop: '1rem' }}>{t('同時に表示する2つ目の言語', 'Second language (shown together)')}</div>
          <select id="second-lang" value={second} onChange={(e) => onSecondChange(e.target.value)} style={selectStyle}>
            <option value="">{t('なし（1言語のみ）', 'None (one language)')}</option>
            {[...MAIN_TARGETS, ...TRIAL_TARGETS].filter((x) => x.code !== target).map((x) => (
              <option key={x.code} value={x.code}>{tl(x)}</option>
            ))}
          </select>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', lineHeight: 1.6 }}>
            {t('セミナー：QRコードが言語ごとに出て、参加者はスマホで自分の言語を選べます（スクリーンは1つ目の言語）。Zoom会議：上部の「会議用字幕（2言語）」を画面共有すると、上段に話した言葉（原文）、下段にもう一方の言語の訳が出ます。※話している言語と同じ言語の字幕は出ません。',
               'Seminars: the QR page shows one code per language so attendees can pick theirs on their phone (the screen shows the first language). Zoom meetings: share "Meeting captions (2 languages)" to show what was said (top) and its translation into the other language (bottom). Note: captions are not shown in the language being spoken.')}
          </p>
        </>
      )}

      <div className="card-title" style={{ marginTop: '1rem' }}>{t('マイクの環境', 'Microphone setup')}</div>
      <select id="noise-mode" value={noise} onChange={(e) => onNoiseChange(e.target.value as NoiseMode)} style={selectStyle}>
        <option value="">{t('標準', 'Standard')}</option>
        <option value="far_field">{t('会場・PCのマイク', 'Room / laptop mic')}</option>
        <option value="near_field">{t('ヘッドセット・ミキサー', 'Headset / mixer')}</option>
      </select>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', lineHeight: 1.6 }}>
        {t('会場・PCのマイク：周りの雑音や反響を抑えます。ヘッドセット・ミキサー：口元のマイクや音響卓からの音声向けです。', 'Room / laptop mic reduces background noise and echo. Headset / mixer is for close-talk mics or audio from a mixing desk.')}
      </p>
    </div>
  );
}
